import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Role } from '@nexa/auth';
import { RoleGate } from '@/auth/role-gate';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { useCareVoid } from '@/lib/disruptions/use-disruptions';
import type { CareBoard, CareCardRow } from '@/lib/disruptions/types';
import { formatMinor, type CareDeskRow, type CareDeskState } from './care-desk-model';
import { CareRedeemDialog } from './care-redeem-dialog';
import { CareTopUpDialog } from './care-top-up-dialog';

// "Cards & tools" — the care desk's money view (feat/care-desk K6, NEX-458):
// four per-currency money tiles (the RosterMetricTiles shape) + the cards table
// in its own contained scroll region. Money honesty rules (§6.6): minor units on
// the wire, currencies NEVER summed across, and `known.wallet:false` renders
// "balances not read" — never a fabricated $0.00. Realtime = WS (`CareChanged`
// merges + the board invalidation) — the "as of" line is the client receive time.

const CARD_STATUS: Record<string, { labelKey: string; variant: BadgeVariant }> = {
  ACTIVE: { labelKey: 'tools.status.active', variant: 'success' },
  /** Degraded: the card exists but the wallet balances were not read. */
  ISSUED: { labelKey: 'tools.status.issued', variant: 'success' },
  PENDING: { labelKey: 'tools.status.pending', variant: 'muted' },
  FAILED: { labelKey: 'tools.status.failed', variant: 'error' },
  DISABLED: { labelKey: 'tools.status.disabled', variant: 'neutral' },
  BLOCKED: { labelKey: 'tools.status.blocked', variant: 'neutral' },
};

/** Board statuses that mean "a card exists / was attempted" — the table's rows. */
const NO_CARD_STATUSES = new Set(['NOT_ISSUED', 'BLOCKED_KYC']);
/** A top-up needs a live spendable card. */
const TOP_UP_STATUSES = new Set(['ACTIVE', 'ISSUED']);
/** Void targets a card the wallet still holds open. */
const VOID_STATUSES = new Set(['ACTIVE', 'ISSUED', 'PENDING']);

export function CareCardsTools({
  desk,
  board,
  boardUpdatedAt,
  disruptionUrn,
  onGoToQueue,
  onRetry,
}: {
  desk: CareDeskState;
  board: CareBoard | null;
  /** Client receive time of the board read (react-query dataUpdatedAt). */
  boardUpdatedAt?: number;
  disruptionUrn: string;
  onGoToQueue: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation('care');
  const deskBySub = useMemo(
    () => new Map(desk.rows.map((d) => [d.row.subCaseUrn, d])),
    [desk.rows],
  );
  const [topUpFor, setTopUpFor] = useState<CareDeskRow | null>(null);
  const [voidFor, setVoidFor] = useState<CareCardRow | null>(null);
  const [redeemFor, setRedeemFor] = useState<CareCardRow | null>(null);

  // ERROR — the board read failed (degrade-open normally makes this rare); the
  // tiles/table have no truth to stand on, so the failure is explicit + actionable.
  if (!board) {
    return (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="care-tools-error">
        <EmptyState
          title={t('tools.error.title')}
          description={t('tools.error.body')}
          action={<Button onClick={onRetry}>{t('actions.retry')}</Button>}
        />
      </div>
    );
  }

  const walletKnown = board.known.wallet !== false;
  const cards = board.cards.filter((c) => !NO_CARD_STATUSES.has(c.status));
  const asOf =
    typeof boardUpdatedAt === 'number' && boardUpdatedAt > 0
      ? new Date(boardUpdatedAt).toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="care-cards-tools">
      {/* The four money tiles — per-currency rows, NEVER summed across currencies. */}
      <div
        className="grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-4"
        role="list"
        aria-label={t('tools.summaryLabel')}
      >
        <MoneyTile
          label={t('tools.tile.entitled')}
          hint={t('tools.tile.entitledHint')}
          rows={board.facts.perCurrency.map((c) => ({ currency: c.currency, minor: c.entitledMinor }))}
          testId="care-tile-entitled"
        />
        <MoneyTile
          label={t('tools.tile.onCards')}
          rows={board.facts.perCurrency.map((c) => ({ currency: c.currency, minor: c.onCardsMinor }))}
          unread={!walletKnown}
          testId="care-tile-on-cards"
        />
        <MoneyTile
          label={t('tools.tile.available')}
          rows={board.facts.perCurrency.map((c) => ({ currency: c.currency, minor: c.availableMinor }))}
          unread={!walletKnown}
          testId="care-tile-available"
        />
        <MoneyTile
          label={t('tools.tile.spent')}
          rows={board.facts.perCurrency.map((c) => ({ currency: c.currency, minor: c.spentMinor }))}
          unread={!walletKnown}
          testId="care-tile-spent"
        />
      </div>
      {asOf ? (
        <p className="shrink-0 text-[11px] text-muted-foreground" data-testid="care-balances-as-of">
          {t('tools.asOf', { time: asOf })}
        </p>
      ) : null}

      {cards.length === 0 ? (
        // EMPTY — no cards yet: point at the two ways money starts moving.
        <EmptyState
          title={t('tools.empty.title')}
          description={t('tools.empty.body')}
          action={
            <Button variant="outline" onClick={onGoToQueue} data-testid="care-tools-go-queue">
              {t('tools.empty.action')}
            </Button>
          }
        />
      ) : (
        // The cards table — its OWN contained scroll region (§18.1): sticky thead,
        // min-width + sideways scroll inside the region, never the page.
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border" data-testid="care-cards-table-region">
          <table className="density-table w-full min-w-[56rem] border-collapse text-sm" data-testid="care-cards-table">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">{t('tools.table.pnr')}</th>
                <th className="px-3 py-2 font-medium">{t('tools.table.holder')}</th>
                <th className="px-3 py-2 font-medium">{t('tools.table.card')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('tools.table.faceValue')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('tools.table.toppedUp')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('tools.table.available')}</th>
                <th className="px-3 py-2 font-medium">{t('tools.table.status')}</th>
                <th className="px-3 py-2 font-medium">{t('tools.table.benefit')}</th>
                <th className="px-3 py-2 font-medium">{t('tools.table.issuedAt')}</th>
                <th className="px-3 py-2 font-medium" aria-label={t('tools.table.actions')} />
              </tr>
            </thead>
            <tbody>
              {cards.map((c) => {
                const status = CARD_STATUS[c.status];
                const statusLabel = status ? t(status.labelKey) : c.status;
                const deskRow = deskBySub.get(c.subCaseUrn);
                return (
                  <tr
                    key={c.subCaseUrn}
                    className="border-b border-border last:border-b-0"
                    data-testid={`care-card-row-${c.pnrLabel}`}
                  >
                    <td className="px-3 py-2 font-mono font-semibold">{c.pnrLabel}</td>
                    <td className="px-3 py-2 text-muted-foreground">{c.holderMasked}</td>
                    <td className="px-3 py-2 font-mono">•• {c.last4 ?? '····'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMinor(c.faceValueMinor, c.currency)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {c.toppedUpMinor > 0 ? formatMinor(c.toppedUpMinor, c.currency) : '—'}
                      {c.pendingTopUp ? (
                        <span
                          className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                          data-testid={`care-card-settling-${c.pnrLabel}`}
                        >
                          {t('settling')}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {walletKnown && c.availableMinor !== undefined
                        ? formatMinor(c.availableMinor, c.currency)
                        : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge size="sm" variant={status?.variant ?? 'muted'}>
                        {statusLabel}
                      </Badge>
                    </td>
                    <td className="px-3 py-2" data-testid={`care-card-benefit-${c.pnrLabel}`}>
                      {c.benefit === 'REDEEMED' ? (
                        <Badge size="sm" variant="success" title={c.redemption ? t('benefit.redeemedAt', { when: new Date(c.redemption.redeemedAt).toLocaleString(), who: c.redemption.redeemedBy }) : undefined}>
                          {c.redemption?.source === 'ISSUER' ? t('benefit.redeemedIssuer') : t('benefit.redeemedSimulated')}
                        </Badge>
                      ) : c.benefit === 'VOIDED' ? (
                        <Badge size="sm" variant="neutral">{t('benefit.voided')}</Badge>
                      ) : c.benefit === 'ISSUED' ? (
                        <span className="text-xs text-muted-foreground">{t('benefit.issued')}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {c.reference ? (
                        <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{c.reference}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {c.issuedAt
                        ? new Date(c.issuedAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span className="flex items-center justify-end gap-1.5">
                        {TOP_UP_STATUSES.has(c.status) && deskRow ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setTopUpFor(deskRow)}
                            data-testid={`care-card-top-up-${c.pnrLabel}`}
                          >
                            {t('tools.topUp')}
                          </Button>
                        ) : null}
                        {TOP_UP_STATUSES.has(c.status) && c.benefit === 'ISSUED' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRedeemFor(c)}
                            data-testid={`care-card-redeem-${c.pnrLabel}`}
                          >
                            {t('tools.redeem')}
                          </Button>
                        ) : null}
                        {VOID_STATUSES.has(c.status) ? (
                          <RoleGate
                            min={Role.OPS_SUPERVISOR}
                            tooltip={t('tools.voidTooltip')}
                          >
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-muted-foreground hover:text-rose-600"
                              onClick={() => setVoidFor(c)}
                              data-testid={`care-card-void-${c.pnrLabel}`}
                            >
                              {t('tools.void')}
                            </Button>
                          </RoleGate>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {topUpFor ? (
        <CareTopUpDialog
          desk={topUpFor}
          disruptionUrn={disruptionUrn}
          board={board}
          onClose={() => setTopUpFor(null)}
        />
      ) : null}
      {redeemFor ? (
        <CareRedeemDialog
          card={redeemFor}
          caseUrn={deskBySub.get(redeemFor.subCaseUrn)?.row.caseUrn}
          disruptionUrn={disruptionUrn}
          {...(redeemFor.reference ? { prefillReference: redeemFor.reference } : {})}
          onClose={() => setRedeemFor(null)}
        />
      ) : null}
      {voidFor ? (
        <CareVoidDialog
          card={voidFor}
          caseUrn={deskBySub.get(voidFor.subCaseUrn)?.row.caseUrn}
          disruptionUrn={disruptionUrn}
          onClose={() => setVoidFor(null)}
        />
      ) : null}
    </div>
  );
}

/** One money tile — per-currency rows inside (never summed), the sky "balances
 *  not read" badge when the wallet composition couldn't read balances. */
function MoneyTile({
  label,
  hint,
  rows,
  unread = false,
  testId,
}: {
  label: string;
  hint?: string;
  rows: Array<{ currency: string; minor: number }>;
  unread?: boolean;
  testId: string;
}) {
  const { t } = useTranslation('care');
  return (
    <Card role="listitem" data-testid={testId}>
      <CardContent className="flex flex-col gap-1 p-4">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
          {hint ? <span className="ml-1 normal-case tracking-normal">· {hint}</span> : null}
        </span>
        {unread ? (
          <span
            className="w-fit rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
            data-testid={`${testId}-unread`}
          >
            {t('balancesNotRead')}
          </span>
        ) : rows.length === 0 ? (
          <span className="text-2xl font-semibold tabular-nums text-muted-foreground">—</span>
        ) : rows.length === 1 && rows[0] ? (
          <span className="text-2xl font-semibold tabular-nums text-foreground">
            {formatMinor(rows[0].minor, rows[0].currency)}
          </span>
        ) : (
          // Multiple currencies: one row per currency — NEVER a summed number.
          <ul className="space-y-0.5">
            {rows.map((r) => (
              <li
                key={r.currency}
                className="flex items-baseline justify-between gap-2 text-sm font-semibold tabular-nums"
              >
                <span className="text-[11px] font-medium uppercase text-muted-foreground">
                  {r.currency}
                </span>
                {formatMinor(r.minor, r.currency)}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Destructive void confirm — supervisor-only (gated upstream), reason mandatory.
 *  A voided card is gone for good; the copy says so verbatim. */
function CareVoidDialog({
  card,
  caseUrn,
  disruptionUrn,
  onClose,
}: {
  card: CareCardRow;
  caseUrn?: string | undefined;
  disruptionUrn: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('care');
  const [reason, setReason] = useState('');
  const [failed, setFailed] = useState(false);
  const voidCard = useCareVoid(disruptionUrn);

  const confirm = async (): Promise<void> => {
    setFailed(false);
    try {
      await voidCard.mutateAsync({
        subCaseUrn: card.subCaseUrn,
        ...(caseUrn ? { caseUrn } : {}),
        reason: reason.trim(),
      });
      onClose();
    } catch {
      // The dialog STAYS OPEN — nothing was saved; the operator retries explicitly.
      setFailed(true);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md" data-testid="care-void-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('voidDialog.title', { last4: card.last4 ?? '····' })}{' '}
            <span className="font-mono">{card.pnrLabel}</span>
          </DialogTitle>
          <DialogDescription>{t('voidDialog.description')}</DialogDescription>
        </DialogHeader>

        <p
          className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
          data-testid="care-void-warning"
        >
          {t('voidDialog.warning')}
        </p>

        <label className="block space-y-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">
            {t('voidDialog.reasonLabel')}
          </span>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1.5"
            placeholder={t('voidDialog.reasonPlaceholder')}
            data-testid="care-void-reason"
          />
        </label>

        {failed ? (
          <p className="text-xs text-destructive" role="alert" data-testid="care-void-error">
            {t('voidDialog.error')}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={voidCard.isPending}>
            {t('voidDialog.keep')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirm()}
            disabled={!reason.trim() || voidCard.isPending}
            data-testid="care-void-confirm"
          >
            {voidCard.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
            {t('voidDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
